import React, { useEffect, useState } from 'react';
import { Badge, Button, Col, Form, Row, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Section from 'components/common/Section';
import { useTranslation } from 'react-i18next';
import { fetchPublicPlans } from 'helpers/plans';
import './LandingPricing.css';

const CURRENCY_MAP = {
  AR: { key: 'price_ars', symbol: '$', suffix: 'ARS' },
  BR: { key: 'price_brl', symbol: 'R$', suffix: 'BRL' },
  US: { key: 'price_usd', symbol: '$', suffix: 'USD' },
  CL: { key: 'price_clp', symbol: '$', suffix: 'CLP' },
  CN: { key: 'price_cny', symbol: '¥', suffix: 'CNY' },
};

const USD_CURRENCY = { key: 'price_usd', symbol: '$', suffix: 'USD' };
const DEFAULT_COUNTRY_DISPLAY = { show_usd: true, show_local: false };
const DEFAULT_PRICING_DISPLAY = {
  AR: { show_usd: true, show_local: true },
  BR: { show_usd: true, show_local: true },
  CL: { show_usd: true, show_local: true },
  CN: { show_usd: true, show_local: false },
  US: { show_usd: true, show_local: false },
};

const resolveCountryFromLocale = value => {
  const lang = String(value || '').toLowerCase();
  if (!lang) return '';
  if (lang.includes('es-cl') || lang.endsWith('-cl')) return 'CL';
  if (lang.includes('es-ar') || lang.endsWith('-ar')) return 'AR';
  if (lang.includes('pt-br') || lang.endsWith('-br')) return 'BR';
  if (lang.includes('en-us') || lang.endsWith('-us')) return 'US';
  if (lang.startsWith('zh') || lang.includes('-cn') || lang.endsWith('-cn')) return 'CN';
  if (lang.startsWith('es')) return 'AR';
  if (lang.startsWith('pt')) return 'BR';
  return '';
};

const resolveCountryFromTimeZone = value => {
  const zone = String(value || '').toLowerCase();
  if (!zone) return '';
  if (zone.includes('argentina') || zone.includes('buenos_aires')) return 'AR';
  if (zone.includes('sao_paulo') || zone.includes('brazil')) return 'BR';
  if (zone.includes('santiago') || zone.includes('chile')) return 'CL';
  if (zone.includes('shanghai') || zone.includes('beijing') || zone.includes('hong_kong') || zone.includes('china')) return 'CN';
  if (zone.includes('new_york') || zone.includes('chicago') || zone.includes('denver') || zone.includes('los_angeles') || zone.includes('us/')) return 'US';
  return '';
};

const LANG_MAP = { en: 'en', es: 'es', pt: 'br', 'pt-BR': 'br', zh: 'zh' };
const DEFAULT_PLAN_COLOR = '#2383f6';
const DEFAULT_PLAN_ICON = 'tag';

const formatPlanLimit = (value, singular, plural) => {
  const normalized = Number(value || 0);
  if (normalized === 0) return `${plural} ilimitados`;
  return `${normalized} ${normalized === 1 ? singular : plural}`;
};

const getAgentAnswersLimit = plan =>
  plan?.has_auto_question_answer === false
    ? null
    : Number(plan?.max_agent_answers_per_month ?? plan?.max_ai_responses_per_month ?? plan?.max_questions_per_month ?? 0);

const getLocalizedValue = (source, baseKey, langCode, fallback = '') =>
  source?.[`${baseKey}_${langCode}`]
  || source?.[`${baseKey}_en`]
  || source?.[baseKey]
  || fallback;

const getLocalizedList = (source, langCode) => {
  if (!source || typeof source !== 'object') return [];
  const value = source[langCode] || source.en || source.es || source.items || [];
  if (!Array.isArray(value)) return [];
  return value
    .map(item => {
      if (item && typeof item === 'object') {
        const text = String(item.text || item.label || '').trim();
        return text ? { text, is_negative: Boolean(item.is_negative || item.negative) } : null;
      }
      const text = String(item || '').trim();
      if (!text) return null;
      return { text, is_negative: text.toLowerCase().includes('sin ') };
    })
    .filter(Boolean);
};

const resolveIcon = icon => {
  const value = String(icon || '').trim();
  if (value.startsWith('fab:')) return ['fab', value.replace('fab:', '')];
  return value || 'tag';
};

const readPrice = (item, key) => parseFloat(item?.[key] || 0);

const resolvePriceDisplay = (item, country, localCurrency, pricingDisplay = DEFAULT_COUNTRY_DISPLAY) => {
  const countryDisplay = pricingDisplay[country] || DEFAULT_COUNTRY_DISPLAY;
  const showUsdConfigured = countryDisplay.show_usd !== false;
  const showLocalConfigured = countryDisplay.show_local === true && country !== 'US';
  const usdPrice = readPrice(item, 'price_usd');
  const localPrice = readPrice(item, localCurrency.key);
  const hasUsdPrice = usdPrice > 0;
  const hasLocalPrice = localPrice > 0;
  const showLocalPrimary = showLocalConfigured && hasLocalPrice;
  const showUsdPrimary = !showLocalPrimary && showUsdConfigured && hasUsdPrice;
  const showSecondaryUsd = showLocalPrimary && showUsdConfigured && hasUsdPrice;
  const showSecondaryLocal = showUsdPrimary && showLocalConfigured && hasLocalPrice;
  return {
    usdPrice,
    localPrice,
    showLocalPrimary,
    showUsdPrimary,
    showSecondaryUsd,
    showSecondaryLocal,
  };
};

const PriceDisplay = ({ item, country, localCurrency, pricingDisplay, monthLabel }) => {
  const price = resolvePriceDisplay(item, country, localCurrency, pricingDisplay);
  if (!price.showLocalPrimary && !price.showUsdPrimary) return <span className="text-muted">-</span>;
  return (
    <>
      <strong>
        {price.showLocalPrimary && (
          <>
            {localCurrency.symbol}
            {price.localPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </>
        )}
        {price.showUsdPrimary && (
          <>
            {USD_CURRENCY.symbol}
            {price.usdPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </>
        )}
        <small>/{monthLabel}</small>
      </strong>
      {price.showSecondaryUsd && (
        <span>≈ {USD_CURRENCY.symbol}{price.usdPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })} {USD_CURRENCY.suffix}</span>
      )}
      {price.showSecondaryLocal && (
        <span>≈ {localCurrency.symbol}{price.localPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })} {localCurrency.suffix}</span>
      )}
    </>
  );
};

const LandingPricing = () => {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [pricingAddons, setPricingAddons] = useState([]);
  const [billing, setBilling] = useState('monthly');
  const [country, setCountry] = useState('US');

  useEffect(() => {
    fetchPublicPlans()
      .then(data => {
        setPlans(data.plans || []);
        setPricingAddons(data.pricing_addons || []);
      })
      .catch(() => {});
  }, []);

  // Detect country from browser/i18n locale with sensible regional fallback.
  useEffect(() => {
    const navigatorCandidates = [
      ...(Array.isArray(navigator.languages) ? navigator.languages : []),
      navigator.language,
    ].filter(Boolean);
    const i18nCandidates = [
      i18n.language,
      ...(Array.isArray(i18n.languages) ? i18n.languages : []),
    ].filter(Boolean);

    const candidates = [...navigatorCandidates, ...i18nCandidates];
    const detectedFromLocale = candidates.map(resolveCountryFromLocale).find(Boolean);
    const detectedFromTimeZone = resolveCountryFromTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const detected = detectedFromLocale || detectedFromTimeZone || 'US';
    if (detected) {
      setCountry(detected);
    }
  }, [i18n.language, i18n.languages]);

  // Resolve localized text helper
  const langCode = LANG_MAP[i18n.language] || LANG_MAP[i18n.language?.split('-')[0]] || 'en';
  const localizedName = (plan) =>
    plan[`name_${langCode}`] || plan.name_en || plan.name;
  const localizedDesc = (plan) => getLocalizedValue(plan, 'description', langCode);
  const localizedCta = (plan) => getLocalizedValue(plan, 'cta_text', langCode, isEnterprisePlan(plan) ? t('LANDING.PRICING_CONTACT') : t('LANDING.PRICING_GET_STARTED'));
  const localizedBadge = (plan) => getLocalizedValue(plan, 'badge_text', langCode, t('LANDING.PRICING_FEATURED', 'Más elegido'));
  const localizedFeatures = (plan) => getLocalizedList(plan.landing_features, langCode);

  const localCurrency = CURRENCY_MAP[country] || CURRENCY_MAP.US;

  // Check if any plan has yearly enabled
  const hasYearly = plans.some(p => p.enable_yearly);

  /**
   * Calculate the per-month price for yearly billing.
   * If yearly_price_usd_override > 0, use that directly as the per-month price.
   * Otherwise, apply yearly_discount_pct to the monthly price.
   */
  const getYearlyMonthlyUsd = (plan) => {
    const override = parseFloat(plan.yearly_price_usd_override || 0);
    if (override > 0) return override;
    const monthly = parseFloat(plan.price_usd || 0);
    const discount = parseFloat(plan.yearly_discount_pct || 0);
    return monthly * (1 - discount / 100);
  };

  const getYearlyMonthlyLocal = (plan, currencyKey) => {
    // For local currencies we apply the same discount percentage
    const monthly = parseFloat(plan[currencyKey] || 0);
    const discount = parseFloat(plan.yearly_discount_pct || 0);
    return monthly * (1 - discount / 100);
  };

  function isEnterprisePlan(plan) {
    return Boolean(plan.is_custom);
  }

  if (plans.length === 0) return null;

  return (
    <Section fluid bg="white" className="text-center py-6 gestodo-pricing-section" id="pricing">
      <div className="gestodo-pricing-heading">
        <h2>El plan perfecto para cada etapa</h2>
        <p>Elige el nivel de control y automatización que tu negocio necesita</p>
      </div>

      {/* Billing toggle */}
      {hasYearly && (
        <div className="d-flex justify-content-center mt-4 mb-2">
          <ToggleButtonGroup
            type="radio"
            name="billing"
            value={billing}
            onChange={v => setBilling(v)}
          >
            <ToggleButton
              id="billing-monthly"
              variant={billing === 'monthly' ? 'primary' : 'outline-primary'}
              value="monthly"
              className="px-4"
            >
              {t('LANDING.PRICING_MONTHLY')}
            </ToggleButton>
            <ToggleButton
              id="billing-yearly"
              variant={billing === 'yearly' ? 'primary' : 'outline-primary'}
              value="yearly"
              className="px-4"
            >
              {t('LANDING.PRICING_YEARLY')}
              <Badge bg="success" className="ms-2">{t('LANDING.PRICING_SAVE')}</Badge>
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}

      <div className="d-flex justify-content-center mb-3">
        <div style={{ minWidth: 220, textAlign: 'left' }}>
          <Form.Label className="mb-1 small text-muted">País / moneda</Form.Label>
          <Form.Select size="sm" value={country} onChange={event => setCountry(event.target.value)}>
            {Object.keys(CURRENCY_MAP).map(code => (
              <option key={code} value={code}>
                {code} - {CURRENCY_MAP[code].suffix}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      <Row className="g-3 mt-3 justify-content-center row-cols-1 row-cols-md-2 row-cols-xl-5 gestodo-pricing-grid">
        {plans.map(plan => {
          const planColor = plan.visual_color || DEFAULT_PLAN_COLOR;
          const planIcon = plan.visual_icon || DEFAULT_PLAN_ICON;
          const features = localizedFeatures(plan);
          const isEnterprise = isEnterprisePlan(plan);
          const isFeatured = Boolean(plan.is_featured);
          const isYearly = billing === 'yearly' && plan.enable_yearly;

          const pricedPlan = {
            ...plan,
            price_usd: isYearly ? getYearlyMonthlyUsd(plan) : parseFloat(plan.price_usd || 0),
            [localCurrency.key]: isYearly ? getYearlyMonthlyLocal(plan, localCurrency.key) : parseFloat(plan[localCurrency.key] || 0),
          };
          const price = resolvePriceDisplay(pricedPlan, country, localCurrency, plan.pricing_display || {});
          const description = localizedDesc(plan);

          return (
            <Col key={plan.slug}>
              <div
                className={`gestodo-pricing-card h-100 ${isFeatured ? 'is-featured' : ''}`}
                style={{ '--pricing-color': planColor }}
              >
                {isFeatured && (
                  <div className="gestodo-pricing-badge">
                    <FontAwesomeIcon icon="star" className="me-1" />
                    {localizedBadge(plan)}
                  </div>
                )}
                <div className="gestodo-pricing-icon">
                  <FontAwesomeIcon icon={resolveIcon(planIcon)} />
                </div>
                <h4 className="gestodo-pricing-name">
                  {localizedName(plan)}
                </h4>
                <p className="pricing-description">
                  {description}
                </p>

                <div className="pricing-price mb-4">
                  {isEnterprise ? (
                    <>
                      <h2>{t('LANDING.PRICING_CUSTOM')}</h2>
                      <p>{localizedCta(plan)}</p>
                    </>
                  ) : (
                    <>
                      <h2>
                        {price.showLocalPrimary && (
                          <>
                            <span>{localCurrency.symbol}</span>
                            {price.localPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            <small>/{t('LANDING.PRICING_MONTH')}</small>
                          </>
                        )}
                        {price.showUsdPrimary && (
                          <>
                            <span>{USD_CURRENCY.symbol}</span>
                            {price.usdPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            <small>/{t('LANDING.PRICING_MONTH')}</small>
                          </>
                        )}
                      </h2>
                      {price.showSecondaryUsd && (
                        <p>
                          ≈ {USD_CURRENCY.symbol}
                          {price.usdPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          {' '}
                          {USD_CURRENCY.suffix}
                        </p>
                      )}
                      {price.showSecondaryLocal && (
                        <p>
                          ≈ {localCurrency.symbol}
                          {price.localPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          {' '}
                          {localCurrency.suffix}
                        </p>
                      )}
                      {!price.showUsdPrimary && !price.showLocalPrimary && (
                        <p>{t('LANDING.PRICING_CUSTOM')}</p>
                      )}
                      {isYearly && (
                        <p>
                          {t('LANDING.PRICING_BILLED_ANNUALLY')}
                        </p>
                      )}
                    </>
                  )}
                </div>

                <ul className="list-unstyled text-start flex-grow-1 pricing-feature-list">
                  {features.map((feature, idx) => {
                    const isNegative = Boolean(feature.is_negative);
                    return (
                      <li key={idx} className={`d-flex align-items-start mb-2 ${isNegative ? 'is-negative' : ''}`}>
                        <FontAwesomeIcon
                          icon={isNegative ? 'minus' : 'check'}
                          className="me-2 mt-1 flex-shrink-0"
                        />
                        <span className={isNegative ? 'text-muted' : ''}>
                          {feature.text}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                <div className="pricing-meta mt-3 pt-3 text-start">
                  <div>
                    <FontAwesomeIcon icon="user" className="me-2" />
                    {isEnterprise ? 'Usuarios ilimitados' : formatPlanLimit(plan.max_users, 'usuario', 'usuarios')}
                  </div>
                  <div>
                    <FontAwesomeIcon icon="store" className="me-2" />
                    {isEnterprise || Number(plan.max_ml_accounts || 0) === 0
                      ? 'Cuentas ilimitadas'
                      : `${plan.max_ml_accounts} cuenta(s) de Mercado Libre`}
                  </div>
                  <div>
                    <FontAwesomeIcon icon="robot" className="me-2" />
                    {getAgentAnswersLimit(plan) == null
                      ? t('LANDING.PRICING_AGENT_ANSWERS_NOT_INCLUDED')
                      : isEnterprise || getAgentAnswersLimit(plan) === 0
                        ? t('LANDING.PRICING_AGENT_ANSWERS_UNLIMITED')
                        : `${getAgentAnswersLimit(plan).toLocaleString()} ${t('LANDING.PRICING_FEAT_AGENT_ANSWERS')}`}
                  </div>
                </div>
                <Button
                  as={Link}
                  to={isEnterprise ? '/waitlist' : '/login'}
                  variant="outline-primary"
                  className="w-100 mt-4 rounded-pill fw-semi-bold pricing-cta"
                >
                  {localizedCta(plan)}
                </Button>
              </div>
            </Col>
          );
        })}
      </Row>
      <div className="gestodo-pricing-addons mt-3">
        <h5>Complementos disponibles</h5>
        <div className="gestodo-pricing-addons-grid">
          {pricingAddons.map(addon => (
            <div
              className="gestodo-pricing-addon"
              key={`${addon.icon}-${getLocalizedValue(addon, 'title', langCode)}`}
              style={{ '--addon-color': addon.color || DEFAULT_PLAN_COLOR }}
            >
              <span className="gestodo-pricing-addon-icon">
                <FontAwesomeIcon icon={resolveIcon(addon.icon)} />
              </span>
              <span className="gestodo-pricing-addon-content">
                <strong>{getLocalizedValue(addon, 'title', langCode)}</strong>
                <span className="pricing-price gestodo-pricing-addon-price">
                  <PriceDisplay
                    item={addon}
                    country={country}
                    localCurrency={localCurrency}
                    pricingDisplay={addon.pricing_display || DEFAULT_PRICING_DISPLAY}
                    monthLabel={t('LANDING.PRICING_MONTH')}
                  />
                </span>
              </span>
            </div>
          ))}
          <div className="gestodo-pricing-note">
            <span className="gestodo-pricing-addon-icon">
              <FontAwesomeIcon icon="shield-alt" />
            </span>
            <span>
              <strong>Sin permanencia. Cancela cuando quieras.</strong>
              <span>Planes flexibles para acompañarte en cada paso de tu crecimiento.</span>
            </span>
          </div>
        </div>
      </div>
      <div className="gestodo-pricing-trust">
        <span><FontAwesomeIcon icon="bolt" /> Activación rápida</span>
        <span><FontAwesomeIcon icon="shield-alt" /> Tus datos siempre seguros</span>
        <span><FontAwesomeIcon icon="headphones" /> Soporte humano real</span>
      </div>
    </Section>
  );
};

export default LandingPricing;
